import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { AnalyticsMetrics } from "../schema/analytics";
import { KpiKey } from "../utils/kpiFilterMap";
import { calcPercent, formatPercent } from "../utils/percentage";

import { KpiHeroTile } from "./KpiHeroTile";

export interface KpiHeroRowProps {
	data: AnalyticsMetrics;
	onKpiClick: (kpi: KpiKey) => void;
}

/**
 * The four headline KPI tiles. The percentages re-base the misleading total-leads denominator
 * (PREC-3568): Assigned is a share of Total, while Replied and Positive are shares of Assigned —
 * the same denominators the Campaign funnel below uses.
 */
export const KpiHeroRow: FunctionComponent<KpiHeroRowProps> = ({ data, onKpiClick }) => {
	const { t } = useTranslation();

	const shareOfAssigned = (value: number): string =>
		`${formatPercent(calcPercent(value, data.assignedLeads), 0)} ${t("analytics.hero.ofAssigned")}`;

	return (
		<div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-5xl mb-4">
			<KpiHeroTile
				clickKpi="totalLeads"
				label={t("analytics.hero.totalLeads")}
				sublabel={formatPercent(100, 0)}
				value={data.totalLeads}
				onKpiClick={onKpiClick}
			/>
			<KpiHeroTile
				clickKpi="assignedLeads"
				label={t("analytics.hero.assignedLeads")}
				sublabel={`${formatPercent(calcPercent(data.assignedLeads, data.totalLeads), 0)} ${t(
					"analytics.hero.ofTotal"
				)}`}
				value={data.assignedLeads}
				onKpiClick={onKpiClick}
			/>
			<KpiHeroTile
				clickKpi="totalRepliedLeads"
				label={t("analytics.hero.replied")}
				sublabel={shareOfAssigned(data.totalRepliedLeads)}
				value={data.totalRepliedLeads}
				onKpiClick={onKpiClick}
			/>
			<KpiHeroTile
				clickKpi="positiveReplies"
				label={t("analytics.hero.positive")}
				sublabel={shareOfAssigned(data.totalPositiveRepliedLeads)}
				value={data.totalPositiveRepliedLeads}
				onKpiClick={onKpiClick}
			/>
		</div>
	);
};
